import React, { Fragment } from 'react'
import moment from 'moment'
import { useCallback, useContext, useFeatures } from 'sb-hooks'
import { NotificationContext } from 'sb-notify-me'
import cx from 'classnames'

import actions from 'redux/actions'
import { constants, links, mixpanel } from 'common/helpers'

import { Box } from 'components/_new/layout'
import { Image, UpchargePrice } from 'components/_new/ui'
import Text from 'components/_new/typography/Text/Text'
import Link from 'components/_new/typography/Link/Link'
import { useConnect } from 'sb-redux-hook'

import messages from './messages'
import s from './Fragrance.scss'


type FragranceNotificationProps = {
  item: {
    name: string
    brand?: string
    image?: string
    img?: string
    url?: string
    upchargePrice?: number
    month?: number
    year?: number
  }
  placement: string
  isQueueItem?: boolean
  onClose?: () => void
}

const FragranceNotification: React.FunctionComponent<FragranceNotificationProps> = (props) => {
  const { item, isQueueItem, onClose, placement } = props

  const { close } = useContext(NotificationContext)

  const { [constants.features.queuePageForNonSubscribed]: isQueuePageForNonSubscribedEnabled } = useFeatures()
  const { isNotSubscribed } = useConnect({
    isNotSubscribed: 'models.subscription.isNotSubscribed',
  })

  const handleClick = useCallback((event) => {
    close()

    // Prevent default mixpanel event
    event.stopPropagation()

    mixpanel.track(constants.analyticsEvents.notificationClick, {
      template: 'perfumeItem',
      type: isQueueItem ? 'queue' : 'cart',
      placement,
    })

    if (isQueueItem) {
      actions.models.router.push(links.queue)
    }

    if (typeof onClose === 'function') {
      onClose()
    }
  }, [ close, isQueueItem, onClose, placement ])

  const { name, brand, img, image, url, upchargePrice, month, year } = item

  const itemImg = img || image || url
  const itemName = brand ? name : 'giftset'
  const itemBrand = brand || name.replace(' giftset', '')
  const scheduledDate = month && year ? `${moment.monthsShort(month - 1)} ${year}` : null

  const showForNonSubscribed = isQueuePageForNonSubscribedEnabled && isNotSubscribed && isQueueItem

  const addInfoClassName = cx({
    [s.addInfoCapitaliseFirstLetter]: showForNonSubscribed,
  })

  return (
    <div className={s.item} onClick={handleClick} data-testid={constants.testIds.common.topRightPopup}>
      <div className={s.preview}>
        {
          Boolean(upchargePrice) && (
            <UpchargePrice className={s.upchargePrice} price={upchargePrice} size="sm" />
          )
        }
        <Image src={itemImg} h={100} x2 x3 resizeBy="height" />
      </div>
      {
        !showForNonSubscribed && (
          <Fragment>
            <div className={s.brand}>{itemBrand}</div>
            <div className={s.name}>{itemName}</div>
          </Fragment>
        )
      }
      {
        isQueueItem && scheduledDate && (
          <Text
            className={addInfoClassName}
            message={{ ...messages.addedToQueue, values: { scheduledDate } }}
            semibold
            size="14-24"
            color={showForNonSubscribed ? 'black' : 'brand'}
          />
        )
      }
      {
        !isQueueItem && (
          <Text
            message={messages.addedToCart}
            semibold
            size="14-24"
            color="brand"
          />
        )
      }
      {
        showForNonSubscribed && (
          <Box mt={4}>
            <Link color="brand">
              <Text size="14-24" tag="span" message={messages.viewQueue} />
            </Link>
          </Box>
        )
      }
    </div>
  )
}


export default FragranceNotification
